/**
 * One Deal Model: the shared identity bar and area tabs, the built-in /
 * read-only notice with "Create editable copy", and the three areas — Editor
 * (the composer, lazily loaded), Data (catalogue, per-model settings and
 * property overrides) and Fixtures & tests.
 *
 * Gated like the library: the `workspace.deal_models` component grant opens
 * it, `deal_model.custom.create` allows copies. In the customer app a system
 * (built-in) model is always inspect-only — edit, test, operate and reset are
 * withheld here, whatever the server grants; only a management build
 * (`--mode management`) passes the server capabilities through unchanged.
 */
import { useNavigate, useParams } from '@tanstack/react-router'
import { IconCopy } from '@tabler/icons-react'
import { parse as parseLosslessJson, stringify as stringifyLosslessJson } from 'lossless-json'
import { useAccess } from '../../api/queries'
import { useCreateDealModel, useDealModel, useDealModelCurrent, type DealModelArea, type DealModelSummary } from '../../api/dealModels'
import { dateShort } from '../../api/format'
import { Shell } from '../../app/Shell'
import { Alert, Button, Skeletons } from '../../ui/kit'
import { NotFoundPage } from '../stubs'
import { DealModelDataCoverage } from './DealModelDataCoverage'
import { DealModelEditorArea } from './DealModelEditorArea'
import { DealModelFixtures } from './DealModelFixtures'
import { WorkspaceHead } from './WorkspaceHead'

export type { DealModelArea } from '../../api/dealModels'

const MODEL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

function LoadingShell({ title }: { title: string }) {
  return (
    <Shell head={<div className="toolbar"><div className="tt"><h1 className="t-page">{title}</h1></div></div>} status={<span>Loading</span>}>
      <div className="page"><Skeletons count={1} kind="block" /><Skeletons count={6} kind="row" /></div>
    </Shell>
  )
}

function MessageShell({ title, children }: { title: string; children: string }) {
  return (
    <Shell head={<div className="toolbar"><div className="tt"><h1 className="t-page">Deal Models</h1></div></div>} status={<span>Deal Models</span>}>
      <Alert title={title}>{children}</Alert>
    </Shell>
  )
}

export function DealModelWorkspace({ area }: { area: DealModelArea }) {
  const params = useParams({ strict: false }) as { modelId?: string }
  const navigate = useNavigate()
  const modelId = typeof params.modelId === 'string' && MODEL_ID.test(params.modelId) ? params.modelId : ''
  const access = useAccess()
  const allowed = access.can('workspace.deal_models')
  const model = useDealModel(modelId, allowed)
  const canCreateCustomModel = access.capability('deal_model.custom.create')
  const source = useDealModelCurrent(modelId, allowed && area === 'editor')
  const createModel = useCreateDealModel()

  if (!modelId) return <NotFoundPage />
  if (access.isPending) return <LoadingShell title="Deal Model" />
  if (access.isError) return <MessageShell title="Workspace access unavailable">{access.error.message}</MessageShell>
  if (!allowed) return <NotFoundPage />
  if (model.isPending) return <LoadingShell title="Deal Model" />
  if (model.isError) return <MessageShell title="Deal Model unavailable">{model.error.message}</MessageShell>
  if (!model.data) return null
  const customerSystemModel = import.meta.env.MODE !== 'management' && model.data.sourceKind === 'system'
  const visibleModel: DealModelSummary = customerSystemModel ? { ...model.data, capabilities: { ...model.data.capabilities, edit: false, test: false, operate: false, reset: false } } : model.data

  const createEditableCopy = async () => {
    if (!canCreateCustomModel) return
    if (!source.data || typeof source.data.sourceDocument !== 'object' || source.data.sourceDocument === null || Array.isArray(source.data.sourceDocument)) return
    const logicalModelId = crypto.randomUUID()
    const suffix = logicalModelId.replaceAll('-', '').slice(0, 8)
    // Deal-model traffic is parsed with lossless-json, so every number in the
    // draft is a `LosslessNumber`. `structuredClone` copies own properties and
    // drops the prototype, which leaves the `isLosslessNumber` flag standing
    // without the `toString()` that carries the digits — `stringify` then emits
    // the bare token `[object Object]` and the POST body is not JSON. Cloning
    // through the same serializer that puts the body on the wire keeps every
    // number exact and keeps the prototype.
    const sourceDocument = parseLosslessJson(stringifyLosslessJson(source.data.sourceDocument) ?? '{}') as Record<string, unknown>
    const originalKey = typeof sourceDocument.modelKey === 'string' ? sourceDocument.modelKey : 'dealModel'
    const modelKey = `${originalKey.slice(0, Math.max(1, 96 - suffix.length))}Copy${suffix}`
    const family = typeof sourceDocument.family === 'string' ? sourceDocument.family : 'custom'
    sourceDocument.logicalModelId = logicalModelId
    sourceDocument.modelKey = modelKey
    const created = await createModel.mutateAsync({
      logicalModelId,
      modelKey,
      family,
      name: `${visibleModel.name} copy`,
      description: visibleModel.description,
      sourceDocument,
    })
    await navigate({ to: '/deal-models/$modelId/editor', params: { modelId: created.logicalModelId }, search: {} })
  }

  if (area === 'editor') {
    const notice = visibleModel.sourceKind === 'system' || !visibleModel.capabilities.edit ? (
      <Alert
        info
        tight
        title={visibleModel.capabilities.edit ? 'Shared built-in model' : 'Read-only model'}
        action={canCreateCustomModel ? (
          <Button small kind="primary" busy={createModel.isPending} disabled={!source.data} onClick={() => { void createEditableCopy().catch(() => undefined) }}>
            <IconCopy className="ic-sm" />Create editable copy
          </Button>
        ) : undefined}
      >
        {visibleModel.sourceKind === 'system' ? 'Built-in calculations are shared. Inspect this model to understand its inputs and outputs.' : 'Inspect this model.'}
        {source.isError ? <span className="dm-error">{source.error.message}</span> : null}
        {createModel.isError ? <span className="dm-error">{createModel.error.message}</span> : null}
      </Alert>
    ) : null
    return <DealModelEditorArea model={visibleModel} notice={notice} />
  }

  return (
    <Shell
      head={<WorkspaceHead model={visibleModel} area={area} />}
      status={<>
        <span>{visibleModel.owner} · {visibleModel.visibility}</span>
        <span>Updated {dateShort(visibleModel.updatedAt) ?? '—'}</span>
        <span className="sp" />
        <span>{area === 'data' ? 'Data' : 'Fixtures & tests'}</span>
      </>}
    >
      {area === 'data'
        ? <DealModelDataCoverage key={visibleModel.logicalModelId} model={visibleModel} />
        : <DealModelFixtures key={visibleModel.logicalModelId} model={visibleModel} />}
    </Shell>
  )
}
